import { useEffect, useState } from "react";
import api, { formatDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Phone } from "lucide-react";
import { toast } from "sonner";

const OUTCOMES = ["interested", "not_interested", "callback", "no_answer", "wrong_number"];
const OUTCOME_STYLE = {
    interested: "text-emerald-700 bg-emerald-50",
    not_interested: "text-gray-600 bg-gray-100",
    callback: "text-amber-700 bg-amber-50",
    no_answer: "text-blue-700 bg-blue-50",
    wrong_number: "text-red-700 bg-red-50",
};

export default function CallLogs() {
    const [rows, setRows] = useState([]);
    const [form, setForm] = useState({ phone: "", outcome: "interested", notes: "" });

    const load = async () => {
        try { const { data } = await api.get("/call-logs"); setRows(data); } catch {}
    };
    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        try {
            await api.post("/call-logs", form);
            toast.success("Call logged");
            setForm({ phone: "", outcome: "interested", notes: "" });
            load();
        } catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
    };

    return (
        <div className="space-y-6" data-testid="calls-page">
            <div>
                <p className="text-[10px] uppercase tracking-[0.28em] text-komma-gold">Telecalling</p>
                <h1 className="font-serif text-3xl text-komma-navy">Call Logs</h1>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <form onSubmit={submit} className="komma-card p-6 space-y-3 lg:col-span-1">
                    <p className="text-[10px] uppercase tracking-[0.24em] text-komma-navy">Log new call</p>
                    <div>
                        <Label className="text-xs uppercase tracking-widest text-komma-navy">Phone</Label>
                        <Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 h-10" data-testid="call-phone" />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest text-komma-navy">Outcome</Label>
                        <Select value={form.outcome} onValueChange={(v) => setForm({ ...form, outcome: v })}>
                            <SelectTrigger className="mt-1.5" data-testid="call-outcome"><SelectValue /></SelectTrigger>
                            <SelectContent>{OUTCOMES.map((o) => <SelectItem key={o} value={o} className="capitalize">{o.replace("_", " ")}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest text-komma-navy">Notes</Label>
                        <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" data-testid="call-notes" />
                    </div>
                    <Button type="submit" className="w-full bg-komma-navy hover:bg-komma-navy-light" data-testid="call-submit">
                        <Phone size={14} className="mr-2" /> Save Call
                    </Button>
                </form>
                <div className="lg:col-span-2 komma-card overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-komma-navy/5">
                            <tr>{["Phone", "Outcome", "Notes", "When"].map((h) => (
                                <th key={h} className="px-4 py-3 text-left text-[10px] uppercase tracking-widest text-komma-navy">{h}</th>
                            ))}</tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.id} className="border-t border-gray-100" data-testid={`call-${r.id}`}>
                                    <td className="px-4 py-3 font-semibold text-komma-navy">{r.phone}</td>
                                    <td className="px-4 py-3">
                                        <span className={`text-xs px-2 py-1 rounded ${OUTCOME_STYLE[r.outcome]}`}>{r.outcome.replace("_", " ")}</span>
                                    </td>
                                    <td className="px-4 py-3 text-gray-600 max-w-xs truncate">{r.notes || "—"}</td>
                                    <td className="px-4 py-3 text-xs text-gray-500">{new Date(r.created_at).toLocaleString()}</td>
                                </tr>
                            ))}
                            {rows.length === 0 && <tr><td colSpan="4" className="p-8 text-center text-gray-500">No calls logged yet.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
